import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Collapse,
  IconFa,
  Text,
} from "@cruk/cruk-react-components";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

import { numberWithCommas } from "@fwa/src/utils/formatUtils";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/TotaliserImpactCarousel/styles.module.css";

import { type PageType } from "@fwa/src/types";

const impactMilestones = [
  {
    value: 25,
    img: "/assets/images/img/impact_statement_25.jpg",
    title: "Kitting out our labs",
    description:
      "£25 could either buy 500 plastic test tubes, 5 boxes of gloves, 5 beakers, or a Bunsen burner, which are the understated tools of many experiments.",
  },
  {
    value: 50,
    img: "/assets/images/img/impact_statement_50.png",
    title: "Unravelling mysteries",
    description:
      "£50 can pay for chemicals called restriction enzymes, which cut long strands of DNA, allowing scientists to find out more about the genes that cause cancer.",
  },
  {
    value: 100,
    img: "/assets/images/img/impact_statement_100.jpg",
    title: "Spotting cancer earlier",
    description:
      "Early diagnosis is essential to getting the right treatment to people with cancer. £100 could pay for a cancer biopsy, where a tiny amount of a lump is removed and sent to be studied in a lab.",
  },
  {
    value: 150,
    img: "/assets/images/img/impact_statement_250.jpg",
    title: "Making diagnosis kinder",
    description:
      "£150 could fund a team of researchers in Manchester for half a day. They're trying to diagnose and monitor lung cancer in a less invasive way.",
  },
  {
    value: 200,
    img: "/assets/images/img/impact_statement_200.png",
    title: "Understanding errors in DNA",
    description:
      "£200 can buy DNA nucleotides, something scientists use to make copies of genes, giving us clues about how things go wrong in cancer.",
  },
  {
    value: 300,
    img: "/assets/images/img/impact_statement_300.png",
    title: "Finding cancer's weakness",
    description:
      "£300 could buy a device used to spin blood samples so different types of cells separate. Our scientists can then study the different types of cancerous cells to find out more about them.",
  },
  {
    value: 500,
    img: "/assets/images/img/impact_statement_500.jpg",
    title: "Providing cancer support",
    description:
      "£500 could run our online moderated forum, Cancer Chat, for a day. It's a safe space open to everyone to discuss any aspect of the disease with others in the cancer community.",
  },
  {
    value: 1000,
    img: "/assets/images/img/impact_statement_1000.jpg",
    title: "Hunting innovations",
    description:
      "To help test potentially life-saving treatments and find the most promising new cancer drugs, £1,000 could buy imaging software that our scientists use to film cells in real time.",
  },
  {
    value: 2500,
    img: "/assets/images/img/impact_statement_2500.jpg",
    title: "Delivering treatments",
    description:
      "The PETReA trial aims to help doctors decide how best to treat lymphoma, a cancer of the white blood cells. £2,500 could fund this trial for five days.",
  },
  {
    value: 5000,
    img: "/assets/images/img/impact_statement_5000.jpg",
    title: "Keeping cancer in remission",
    description:
      "£5,000 could fund the Add-Aspirin trial for five days. This clinical trial is investigating whether aspirin could stop some types of cancer coming back after treatment.",
  },
];

export const TotaliserImpactCarousel = ({ page }: { page: PageType }) => {
  const [currentOpen, setCurrentOpen] = useState<number | null>(null);

  const scrollRef = useRef<HTMLUListElement>(null);
  const maxFromTargetOrTotalValue = Math.max(
    page.donationsTotalAmount || 0,
    page.target || 0,
  );

  const achievedMilestonesAndNextMilestone = impactMilestones.slice(
    0,
    impactMilestones.filter(
      (milestone) => milestone.value <= maxFromTargetOrTotalValue,
    ).length + 1,
  );

  const filteredImpactStatements =
    !page.target && page.donationsTotalAmount === 0
      ? impactMilestones
      : achievedMilestonesAndNextMilestone;

  const toggleCollapse = (index: number) => {
    if (currentOpen !== index) {
      setCurrentOpen(index);
    }
    if (currentOpen === index) {
      setCurrentOpen(null);
    }
  };

  useEffect(() => {
    if (page?.donationsTotalAmount) {
      const scrollElement = scrollRef.current;
      if (!scrollElement || !page?.donationsTotalAmount) return;

      const currentItem = filteredImpactStatements.findIndex(
        (item) => item.value > page?.donationsTotalAmount,
      );
      if (currentItem < 0) return;

      const element = scrollElement.children[currentItem] as HTMLDivElement;
      if (!element) return;

      const scrollX = element.offsetLeft - scrollElement.clientWidth / 2;
      scrollElement.scrollTo({ top: 0, left: scrollX, behavior: "instant" });
    }
  }, [page?.donationsTotalAmount]);

  return (
    <Box marginBottom="xxl">
      <div style={{ position: "relative" }} id="totaliser_impact_milestones">
        <ul
          className={styles["carousel-scroll-area"]}
          ref={scrollRef}
          aria-live="polite"
          aria-label="Fundraising impact"
          aria-roledescription="Carousel"
          tabIndex={0}
        >
          {filteredImpactStatements.map((milestone, index) => (
            <li
              className={styles["carousel-card"]}
              key={milestone.value}
              data-is-first={index === 0}
              data-is-last={index === filteredImpactStatements.length - 1}
            >
              <Collapse
                id={`milestone-${index}`}
                aria-label={`£${milestone.value}`}
                headerTitleText={milestone.title}
                startOpen={currentOpen === index}
                onOpenChange={() => toggleCollapse(index)}
                headerComponent={
                  <Box margin="none" padding="none" marginVertical="xxs">
                    <div className={sharedStyles["column-align-center"]}>
                      <Box marginBottom="s">
                        <div
                          className={styles["rounded-image-wrapper"]}
                          data-is-achieved={
                            milestone.value <= page.donationsTotalAmount
                          }
                        >
                          <img src={milestone.img} alt="" />
                        </div>
                        <div className={sharedStyles["row-center"]}>
                          <div className={styles["badge-wrapper"]}>
                            <Badge
                              backgroundColor="background-mid"
                              textColor="primary"
                              aria-hidden="true"
                            >
                              {`£${numberWithCommas(milestone.value)}`}
                            </Badge>
                          </div>
                        </div>
                      </Box>
                      <Text
                        textColor="primary"
                        textAlign="center"
                        paddingBottom="none"
                        marginBottom="none"
                      >
                        {milestone.title}
                      </Text>
                      <div
                        className={styles["flipping-icon"]}
                        data-is-open={currentOpen === index}
                      >
                        <IconFa faIcon={faChevronDown} />
                      </div>
                    </div>
                  </Box>
                }
              >
                <Text>{milestone.description}</Text>
              </Collapse>
            </li>
          ))}
        </ul>
        <div className={styles["carousel-fade"]} />
      </div>
    </Box>
  );
};
export default TotaliserImpactCarousel;
